import { Helmet } from "react-helmet-async";
import { useLocation, useParams } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { usePaginaEstatica } from "../hooks/useCms";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Renderizador genérico para entradas del modelo PaginaEstatica del CMS.
 * Sirve para política-privacidad, aviso-legal, cookies y cualquier
 * página que el equipo añada en el futuro desde el admin.
 *
 * Las páginas que tienen tratamiento especial (homepage, programas, etc.)
 * NO usan este componente: tienen su propia ruta.
 */
export function PaginaEstaticaPage() {
  const params = useParams<{ slug?: string }>();
  const location = useLocation();
  // Si la ruta es dinámica (/paginas/:slug) usamos el param;
  // si es una ruta fija (/politica-privacidad, /aviso-legal, /cookies)
  // derivamos el slug del propio pathname.
  const slug = params.slug ?? location.pathname.replace(/^\/+|\/+$/g, "");
  const { data, isLoading } = usePaginaEstatica(slug);

  if (!slug) return <NotFoundPage />;

  if (isLoading) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando…</p>
      </section>
    );
  }

  if (!data) {
    // No existe en el CMS o todavía no la has publicado.
    return <NotFoundPage />;
  }

  const seoTitle = data.seoTitle ?? data.titulo;
  const seoDescription = data.seoDescription ?? `${data.titulo} — Campus EMJC`;

  return (
    <>
      <Helmet>
        <title>{seoTitle} — Campus EMJC</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <article className="container-emjc py-20">
        <header>
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            Información legal
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight">
            {data.titulo}
          </h1>
          {data.ultimaActualizacion && (
            <p className="mt-3 text-sm text-gray-700">
              Última actualización:{" "}
              <time dateTime={data.ultimaActualizacion}>
                {new Date(data.ultimaActualizacion).toLocaleDateString("es-ES", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </time>
            </p>
          )}
        </header>

        <div className="mt-10">
          <BlocksRenderer blocks={data.cuerpo} />
        </div>
      </article>
    </>
  );
}
